import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import api from "../../services/api";
import { download, ExportButton, fileName, rangeLabel, tableSheet } from "./excel";

const money = (value) => (value === null || value === undefined ? "-" : Number(value).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 }));
const qty = (value) => (value === null || value === undefined ? "-" : Number(value).toLocaleString("en-IN", { maximumFractionDigits: 3 }));

const VIEWS = {
  product: {
    label: "Product Wise",
    endpoint: "/api/v1/reports/item-wise-sales",
    rowKey: (row) => row.productId,
    columns: [
      { key: "skuCode", label: "SKU" },
      { key: "productName", label: "Product" },
      { key: "quantitySold", label: "Qty Sold", format: "qty" },
      { key: "revenue", label: "Revenue", format: "money" },
    ],
  },
  invoice: {
    label: "Invoice Wise",
    endpoint: "/api/v1/reports/item-wise-sales/invoice-wise",
    rowKey: (row, i) => `${row.invoiceId}-${row.productId}-${i}`,
    columns: [
      { key: "invoiceNumber", label: "Invoice #" },
      { key: "invoiceDate", label: "Date" },
      { key: "customerName", label: "Customer" },
      { key: "skuCode", label: "SKU" },
      { key: "productName", label: "Product" },
      { key: "quantity", label: "Qty", format: "qty" },
      { key: "unitPrice", label: "Unit Price", format: "money" },
      { key: "basicAmount", label: "Basic Amount", format: "money" },
      { key: "cgstAmount", label: "CGST", format: "money" },
      { key: "sgstAmount", label: "SGST", format: "money" },
      { key: "igstAmount", label: "IGST", format: "money" },
      { key: "schemeDiscount", label: "Scheme Discount", format: "money" },
      { key: "cashDiscount", label: "Cash Discount", format: "money" },
      { key: "lineTotal", label: "Line Total (incl. tax)", format: "money" },
    ],
  },
  month: {
    label: "Month Wise",
    endpoint: "/api/v1/reports/item-wise-sales/month-wise",
    rowKey: (row) => `${row.month}-${row.productId}`,
    columns: [
      { key: "month", label: "Month" },
      { key: "skuCode", label: "SKU" },
      { key: "productName", label: "Product" },
      { key: "quantitySold", label: "Qty Sold", format: "qty" },
      { key: "revenue", label: "Revenue", format: "money" },
    ],
  },
};

const formatCell = (value, column) => {
  if (value === null || value === undefined || value === "") return "-";
  if (column.format === "money") return money(value);
  if (column.format === "qty") return qty(value);
  return String(value);
};

const ItemWiseSalesReport = () => {
  const [view, setView] = useState("product");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  // the view and dates the rows on screen were loaded with — the export follows these, not half-edited filters
  const [applied, setApplied] = useState(null);

  const config = VIEWS[view];

  const exportExcel = () => {
    const loadedView = VIEWS[applied.view];
    const title = `Product Wise Sales — ${loadedView.label}`;
    const filters = rangeLabel(applied.fromDate, applied.toDate);
    download(fileName(`Product Wise Sales ${loadedView.label}`, filters), [tableSheet(title, filters, loadedView.columns, rows, loadedView.label)]);
  };

  const load = async () => {
    setLoading(true);
    try {
      const data = await api.get(config.endpoint, { fromDate: fromDate || undefined, toDate: toDate || undefined });
      setRows(Array.isArray(data) ? data : []);
      setApplied({ view, fromDate, toDate });
    } catch (error) {
      toast.error(error.message || "Failed to load report");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view]);

  const totals = config.columns.reduce((acc, column) => {
    if (column.format === "money" || column.format === "qty") {
      acc[column.key] = rows.reduce((sum, row) => sum + (Number(row[column.key]) || 0), 0);
    }
    return acc;
  }, {});

  return (
    <div className="p-4" style={{ position: "relative" }}>
      {loading ? (
        <div className="loading-overlay">
          <div className="spinner-card">
            <div className="spinner-ring" />
            <div className="spinner-label">Loading report...</div>
          </div>
        </div>
      ) : null}

      <div className="page-subtitle" style={{ marginBottom: "12px" }}>
        Sales by product — view as a totals-per-product rollup, every individual invoice line, or a per-product
        monthly trend.
      </div>

      <div className="filters-bar">
        <select value={view} onChange={(e) => setView(e.target.value)} aria-label="Report view">
          {Object.entries(VIEWS).map(([key, v]) => (
            <option key={key} value={key}>
              {v.label}
            </option>
          ))}
        </select>
        <input type="date" onClick={(e) => e.target.showPicker?.()} value={fromDate} onChange={(e) => setFromDate(e.target.value)} aria-label="From date" />
        <input type="date" onClick={(e) => e.target.showPicker?.()} value={toDate} onChange={(e) => setToDate(e.target.value)} aria-label="To date" />
        <button className="primary" onClick={load} disabled={loading}>
          {loading ? "Loading..." : "Run Report"}
        </button>
        <ExportButton onClick={exportExcel} disabled={loading || rows.length === 0 || applied?.view !== view} />
      </div>

      <div className="table-wrapper">
        <table>
          <thead>
            <tr>
              {config.columns.map((column) => (
                <th key={column.key} style={{ textAlign: "center" }}>
                  {column.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td colSpan={config.columns.length}>
                  <div className="empty-state">
                    <span className="empty-state-title">No data for this period</span>
                  </div>
                </td>
              </tr>
            ) : (
              rows.map((row, index) => (
                <tr key={config.rowKey(row, index)}>
                  {config.columns.map((column) => (
                    <td key={column.key} style={{ textAlign: "center" }}>
                      {formatCell(row[column.key], column)}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
          {rows.length > 0 ? (
            <tfoot>
              <tr>
                {config.columns.map((column, index) => (
                  <td key={column.key} style={{ fontWeight: 700, textAlign: "center" }}>
                    {index === 0 ? "Total" : totals[column.key] !== undefined ? formatCell(totals[column.key], column) : ""}
                  </td>
                ))}
              </tr>
            </tfoot>
          ) : null}
        </table>
      </div>
    </div>
  );
};

export default ItemWiseSalesReport;
